<template>
    <div class="mobile-navbar fixed bottom-0 left-0 right-0 bg-base-100 border-t border-base-200 z-50 md:hidden">
        <!-- 子菜单遮罩 -->
        <div v-if="showSubmenu" class="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" @click="hideSubmenu"></div>
        
        <!-- 子菜单 -->
        <transition name="submenu">
            <div v-if="showSubmenu && activeSubmenuItems.length > 0" 
                 class="absolute bottom-full left-2 right-2 mb-2 bg-base-100 rounded-2xl shadow-2xl border border-base-200/60 z-50 overflow-hidden backdrop-blur-lg">
                <!-- 子菜单标题 -->
                <div class="px-6 py-4 bg-gradient-to-r from-primary/10 to-secondary/10 border-b border-base-200/50">
                    <div class="flex items-center justify-between">
                        <h3 class="text-lg font-semibold text-base-content flex items-center">
                            <div class="w-6 h-6 mr-3 text-primary">
                                <svg viewBox="0 0 24 24" fill="currentColor" class="w-full h-full">
                                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                                </svg>
                            </div>
                            {{ $tt(currentSubmenuTitle) }}
                        </h3>
                        <button @click="hideSubmenu" class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-base-200/80 transition-colors">
                            <svg class="w-5 h-5 text-base-content/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                            </svg>
                        </button>
                    </div>
                </div>
                
                <!-- 子菜单内容 -->
                <div class="max-h-80 overflow-y-auto scrollbar-hide">
                    <div class="grid grid-cols-2 gap-1 p-2">
                        <div v-for="(subItem, index) in activeSubmenuItems" 
                             :key="index"
                             class="submenu-item rounded-xl transition-all duration-300 cursor-pointer group"
                             :class="isSubmenuItemActive(subItem) ? 'bg-gradient-to-br from-primary/20 to-primary/10 border-primary/30 shadow-lg' : 'hover:bg-base-200/60 hover:shadow-md'"
                             @click="handleSubmenuClick(subItem)">
                            <div class="flex flex-col items-center p-4 space-y-2">
                                <div class="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300"
                                     :class="isSubmenuItemActive(subItem) ? 'bg-primary/20 text-primary scale-110' : 'bg-base-200/50 text-base-content/70 group-hover:bg-primary/10 group-hover:text-primary/80 group-hover:scale-105'">
                                    <component :is="'div'" 
                                               class="w-5 h-5" 
                                               v-if="subItem.iconSvg"
                                               v-html="subItem.iconSvg">
                                    </component>
                                    <svg-icon v-else :name="subItem.iconName" class="w-5 h-5 fill-current"></svg-icon>
                                </div>
                                <span class="text-sm font-medium text-center leading-tight transition-colors duration-300"
                                      :class="isSubmenuItemActive(subItem) ? 'text-primary' : 'text-base-content group-hover:text-primary/80'">
                                    {{ $tt(subItem.name) }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                
                <!-- 底部装饰 -->
                <div class="h-1 bg-gradient-to-r from-primary/20 via-secondary/20 to-primary/20"></div>
            </div>
        </transition>

        <div class="flex items-center justify-around py-3">
            <div 
                v-for="(item, index) in navItems" 
                :key="index"
                class="flex items-center justify-center min-w-0 flex-1 nav-item relative"
                :class="{ 
                    'text-primary active': isActive(item.pathName),
                    'has-submenu': isActive(item.pathName) && item.submenu && item.submenu.length > 0
                }"
                @click="handleNavClick(item, $event)"
            >
                <div class="w-8 h-8 flex items-center justify-center">
                    <svg 
                        class="w-6 h-6 transition-colors duration-200" 
                        :class="{ 'text-primary': isActive(item.pathName) }"
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                    >
                        <path 
                            stroke-linecap="round" 
                            stroke-linejoin="round" 
                            stroke-width="2" 
                            :d="item.icon"
                        />
                    </svg>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import SvgIcon from '@/components/SvgIcon.vue';

export default {
    name: 'MobileNavbar',
    components: {SvgIcon},
    props: {
        role: {
            type: String,
            default: 'V1'
        },
        isSuperuser: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            showSubmenu: false,
            activeSubmenuItems: [],
            currentSubmenuTitle: ''
        }
    },
    computed: {
        navItems() {
            const allItems = [
                {
                    name: '首页',
                    pathName: 'index',
                    role: 'V2',
                    icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m0 0V11a1 1 0 011-1h2a1 1 0 011 1v10m-6 0h6m0 0h3a1 1 0 001-1V10'
                },
                {
                    name: '操作台',
                    pathName: 'home',
                    role: 'V1',
                    icon: 'M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2',
                    submenu: [
                        {
                            name: '文件管理',
                            action: 'showFileSection',
                            iconSvg: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M10,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V8C22,6.89 21.1,6 20,6H12L10,4Z"/></svg>'
                        },
                        {
                            name: '批量处理',
                            action: 'showEditSection',
                            iconSvg: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/></svg>'
                        }
                    ]
                },
                {
                    name: '操作记录',
                    pathName: 'log',
                    role: 'V1',
                    icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                },
                {
                    name: '音乐收藏',
                    pathName: 'library',
                    role: 'V2',
                    icon: 'M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3',
                    submenu: [
                        {
                            name: '首页',
                            path: '/discover/',
                            iconSvg: '<svg viewBox="0 0 1024 1024" fill="currentColor"><path d="M512 300.8C396.8 300.8 307.2 396.8 307.2 512c0 57.6 25.6 115.2 70.4 153.6 12.8 12.8 32 12.8 44.8 0 12.8-12.8 12.8-32 0-44.8C384 595.2 364.8 556.8 364.8 512c0-83.2 64-147.2 147.2-147.2 83.2 0 147.2 64 147.2 147.2 0 83.2-64 147.2-147.2 147.2L505.6 659.2c-12.8 0-25.6 6.4-25.6 19.2-6.4 19.2-19.2 38.4-32 51.2-12.8 12.8-12.8 32 0 44.8 6.4 6.4 12.8 6.4 19.2 6.4 6.4 0 12.8 0 19.2-6.4 19.2-19.2 32-38.4 38.4-57.6 108.8-6.4 198.4-96 198.4-211.2C723.2 396.8 627.2 300.8 512 300.8z"/></svg>'
                        },
                        {
                            name: '正在播放',
                            path: '/playing/local',
                            iconSvg: '<svg viewBox="0 0 1024 1024" fill="currentColor"><path d="M64 511.5C64 263.8 264.8 63 512.5 63S961 263.8 961 511.5V752c0 61.856-50.144 112-112 112h-56c-61.856 0-112-50.144-112-112V584c0-61.856 50.144-112 112-112h85.908C859.199 287.057 702.673 143 512.5 143S165.8 287.057 146.092 472H232c61.856 0 112 50.144 112 112v168c0 61.856-50.144 112-112 112h-56c-61.856 0-112-50.144-112-112V512v-0.25-0.25z"/></svg>'
                        },
                        {
                            name: '播放列表',
                            path: '/playlists/recently-added/',
                            iconSvg: '<svg viewBox="0 0 1024 1024" fill="currentColor"><path d="M896 853.333333H597.333333v-85.333333h298.666667z m0-256H597.333333v85.333334h298.666667z m0-170.666666H597.333333v85.333333h298.666667z m-213.333333-42.666667a256 256 0 0 1-170.666667-65.493333V704a192 192 0 1 1-85.333333-159.573333V128h85.333333a170.666667 170.666667 0 0 0 170.666667 170.666667z"/></svg>'
                        },
                        {
                            name: '多端同播',
                            path: '/groupcast',
                            iconSvg: '<svg viewBox="0 0 1024 1024" fill="currentColor"><path d="M753.564731 337.471035c-45.8697 0-160.259984 113.849978-243.789399 194.548928C383.134027 654.383848 263.508509 773.284865 167.764911 773.284865l-58.892295 0c-24.068162 0-43.581588-19.526729-43.581588-43.581588s19.513426-43.581588 43.581588-43.581588l58.892295 0c60.504002 0 183.002964-121.68134 281.432741-216.784348 119.79641-115.744117 223.254713-219.029482 304.368102-219.029482l56.209186 0-59.641355-57.828057c-17.033955-16.993023-17.060561-42.902112-0.057305-59.927881 17.002232-17.030885 44.596707-17.064654 61.631686-0.065492l134.207631 133.874033c8.192589 8.172123 12.794397 19.238157 12.794397 30.803563 0 11.564383-4.601808 22.604834-12.794397 30.776957L811.706943 461.72599c-8.505721 8.486278-19.646456 12.522198-30.78719 12.522198-11.166317 0-22.333658-4.676509-30.844495-13.199627-17.003256-17.025769-16.975627-45.432749 0.057305-62.425771l59.641355-61.151755L753.564731 337.471035z"/></svg>'
                        },
                        {
                            name: '专辑',
                            path: '/albums/recently-added/',
                            iconSvg: '<svg viewBox="0 0 1024 1024" fill="currentColor"><path d="M511.701333 511.701333m-85.632 0a85.632 85.632 0 1 0 171.264 0 85.632 85.632 0 1 0-171.264 0Z"></path><path d="M512 85.333333C276.736 85.333333 85.333333 276.736 85.333333 512s191.402667 426.666667 426.666667 426.666667 426.666667-191.402667 426.666667-426.666667S747.264 85.333333 512 85.333333z m0 768c-188.202667 0-341.333333-153.130667-341.333333-341.333333s153.130667-341.333333 341.333333-341.333333 341.333333 153.130667 341.333333 341.333333-153.130667 341.333333-341.333333 341.333333z"></path></svg>'
                        },
                        {
                            name: '艺术家',
                            path: '/artists/most-albums',
                            iconSvg: '<svg viewBox="0 0 1024 1024" fill="currentColor"><path d="M535.253333 255.274667a170.666667 170.666667 0 0 0 234.154667 233.045333c-51.2-23.850667-104.618667-61.866667-138.24-95.488-34.048-34.048-72.149333-87.04-95.914667-137.557333z m65.706667-63.829334c12.928 40.277333 57.301333 107.818667 90.538667 141.056 32.682667 32.682667 99.072 76.074667 141.44 89.813334a170.666667 170.666667 0 0 0-231.936-230.869334z"/></svg>'
                        },
                        {
                            name: '歌曲',
                            path: '/tracks/recently-added/',
                            iconSvg: '<svg viewBox="0 0 1024 1024" fill="currentColor"><path d="M768 436.906667v53.76a21.333333 21.333333 0 0 1-21.333333 21.333333h-42.666667a21.333333 21.333333 0 0 1-21.333333-21.333333v-11.52a115.626667 115.626667 0 0 0-64-100.693334c-7.68-4.266667-14.506667-9.386667-21.333334-14.08v384A161.28 161.28 0 0 1 426.666667 896a161.28 161.28 0 0 1-170.666667-149.333333 161.28 161.28 0 0 1 170.666667-149.333334 183.04 183.04 0 0 1 85.333333 20.906667V149.333333a21.333333 21.333333 0 0 1 21.333333-21.333333h42.666667a21.333333 21.333333 0 0 1 21.333333 21.333333v7.253334a113.92 113.92 0 0 0 61.013334 103.253333A197.12 197.12 0 0 1 768 436.906667z"/></svg>'
                        },
                        {
                            name: '风格',
                            path: '/genres/a-z/',
                            iconSvg: '<svg viewBox="0 0 1024 1024" fill="currentColor"><path d="M0 284.444444v455.111112a56.888889 56.888889 0 0 0 113.777778 0V284.444444a56.888889 56.888889 0 1 0-113.777778 0zM910.222222 284.444444v455.111112a56.888889 56.888889 0 0 0 113.777778 0V284.444444a56.888889 56.888889 0 0 0-113.777778 0zM455.111111 56.888889v910.222222a56.888889 56.888889 0 0 0 113.777778 0V56.888889a56.888889 56.888889 0 1 0-113.777778 0z"/></svg>'
                        },
                        {
                            name: '收藏',
                            path: '/favourites/albums/',
                            iconSvg: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>'
                        }
                    ]
                },
                {
                    name: '系统设置',
                    pathName: 'system',
                    role: 'V2',
                    icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
                    submenu: [
                        {
                            name: 'system.menu.basic',
                            action: 'index:1',
                            iconName: 'sys-home'
                        },
                        {
                            name: 'system.menu.scraping',
                            action: 'index:2',
                            iconName: 'sys-backend'
                        },
                        {
                            name: 'system.menu.tasks',
                            action: 'index:3',
                            iconName: 'sys-backend-task'
                        },
                        {
                            name: 'system.menu.xiaoai',
                            action: 'index:4',
                            iconName: 'sys-xiaoai'
                        },
                        {
                            name: 'system.menu.webdav',
                            action: 'index:5',
                            iconName: 'sys-webdav'
                        },
                        {
                            name: 'system.menu.openapi',
                            action: 'index:7',
                            iconName: 'sys-openapi'
                        },
                        {
                            name: 'system.menu.permission',
                            action: 'index:8',
                            iconName: 'user'
                        },
                        {
                            name: 'system.menu.personalization',
                            action: 'index:9',
                            iconName: 'settings'
                        },
                        {
                            name: 'system.menu.info',
                            action: 'index:6',
                            iconName: 'sys-info'
                        },
                    ]
                }
            ]

            if (this.role === 'V2' && this.isSuperuser) {
                return allItems
            } else {
                return allItems.filter(item => this.role === item.role)
            }
        },
        currentRouteName() {
            const name = this.$route.meta.hasOwnProperty('fatherName') ? this.$route.meta.fatherName : this.$route.name
            return name
        }
    },
    methods: {
        isActive(pathName) {
            return this.currentRouteName === pathName
        },
        handleNavClick(item, event) {
            // 如果已经是激活状态且有子菜单，显示子菜单
            if (this.isActive(item.pathName) && item.submenu && item.submenu.length > 0) {
                if (!this.isSuperuser && item.pathName === 'system') {
                    return
                }
                this.showSubmenuItems(item)
                return
            }

            // 添加点击动画效果
            const target = event.currentTarget
            target.classList.add('nav-item-clicked')
            
            // 200ms后移除动画类
            setTimeout(() => {
                target.classList.remove('nav-item-clicked')
            }, 200)

            // 导航逻辑
            if (item.pathName === 'library') {
                this.$router.push({ path: '/discover/' })
            } else {
                this.$router.push({ name: item.pathName })
            }
            
            // 隐藏子菜单
            this.hideSubmenu()
        },
        showSubmenuItems(item) {
            this.activeSubmenuItems = item.submenu || []
            this.currentSubmenuTitle = item.name
            this.showSubmenu = true
        },
        hideSubmenu() {
            this.showSubmenu = false
            this.activeSubmenuItems = []
            this.currentSubmenuTitle = ''
        },
        handleSubmenuClick(subItem) {
            if (subItem.action) {
                // 如果有 action 属性，通过 Vuex store 触发对应的方法
                this.$store.commit('triggerHomeAction', subItem.action)
            } else if (subItem.path) {
                // 否则执行路由跳转
                this.$router.push({ path: subItem.path })
            }
            this.hideSubmenu()
        },
        isSubmenuItemActive(subItem) {
            const currentPath = this.$route.path
            // 精确匹配或者路径前缀匹配

            if (currentPath === subItem.path) return true
            
            return false
        }
    },
    mounted() {
        // 点击外部隐藏子菜单
        document.addEventListener('click', (e) => {
            if (!this.$el.contains(e.target)) {
                this.hideSubmenu()
            }
        })
    },
    beforeDestroy() {
        document.removeEventListener('click', this.hideSubmenu)
    }
}
</script>

<style scoped>
.mobile-navbar {
    height: 60px;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
}

/* 子菜单动画 - 现代化弹跳效果 */
.submenu-enter-active {
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    transform-origin: center bottom;
}

.submenu-leave-active {
    transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    transform-origin: center bottom;
}

.submenu-enter-from {
    opacity: 0;
    transform: translateY(20px) scale(0.9) rotateX(-10deg);
    filter: blur(4px);
}

.submenu-leave-to {
    opacity: 0;
    transform: translateY(10px) scale(0.95);
    filter: blur(2px);
}

.submenu-enter-to,
.submenu-leave-from {
    opacity: 1;
    transform: translateY(0) scale(1) rotateX(0deg);
    filter: blur(0px);
}

/* 单个导航项的点击动画效果 */
.nav-item {
    transition: all 0.2s ease-in-out;
    border-radius: 12px;
    cursor: pointer;
    position: relative;
}

.nav-item-clicked {
    transform: scale(0.85);
    background-color: rgba(var(--p) / 0.1);
}

/* 激活状态的样式 */
.nav-item.active::before {
    content: '';
    position: absolute;
    top: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 4px;
    height: 4px;
    background: hsl(var(--p));
    border-radius: 50%;
    animation: slideIn 0.3s ease-out;
}

/* 子菜单指示器 */
.nav-item.active.has-submenu::after {
    content: '';
    position: absolute;
    top: -6px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 4px solid hsl(var(--p));
    animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateX(-50%) translateY(4px);
    }
    to {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
    }
}

/* 图标的悬停效果 */
.nav-item svg {
    transition: transform 0.2s ease-in-out;
}

.nav-item.active svg {
    transform: scale(1.1);
}

/* 子菜单项样式 - 现代化卡片风格 */
.submenu-item {
    position: relative;
    border: 1px solid transparent;
    overflow: hidden;
}

.submenu-item::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s;
}

.submenu-item:hover::before {
    left: 100%;
}

.submenu-item:active {
    transform: scale(0.95);
}

/* 活跃状态的特殊效果 */
.submenu-item.bg-gradient-to-br {
    border: 1px solid hsl(var(--p) / 0.3);
    position: relative;
}

.submenu-item.bg-gradient-to-br::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 0;
    height: 0;
    border-top: 8px solid hsl(var(--p));
    border-left: 8px solid transparent;
}

/* 隐藏滚动条 */
.scrollbar-hide {
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
}

.scrollbar-hide::-webkit-scrollbar {
    display: none;  /* Chrome, Safari and Opera */
}

/* 子菜单标题图标旋转动画 */
.submenu-enter-active h3 svg {
    animation: rotate 0.6s ease-out;
}

@keyframes rotate {
    from {
        transform: rotate(-180deg) scale(0.5);
        opacity: 0;
    }
    to {
        transform: rotate(0deg) scale(1);
        opacity: 1;
    }
}

/* 子菜单项入场动画 */
.submenu-enter-active .submenu-item {
    animation: slideInUp 0.4s ease-out;
    animation-delay: calc(var(--item-index, 0) * 0.05s);
    animation-fill-mode: backwards;
}

@keyframes slideInUp {
    from {
        opacity: 0;
        transform: translateY(20px) scale(0.8);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

/* 给子菜单项添加延迟动画 */
.submenu-item:nth-child(1) { --item-index: 1; }
.submenu-item:nth-child(2) { --item-index: 2; }
.submenu-item:nth-child(3) { --item-index: 3; }
.submenu-item:nth-child(4) { --item-index: 4; }
.submenu-item:nth-child(5) { --item-index: 5; }
.submenu-item:nth-child(6) { --item-index: 6; }
.submenu-item:nth-child(7) { --item-index: 7; }
.submenu-item:nth-child(8) { --item-index: 8; }
.submenu-item:nth-child(9) { --item-index: 9; }

/* 子菜单关闭按钮悬停效果 */
.submenu-enter-active button {
    animation: fadeInScale 0.5s ease-out 0.2s both;
}

@keyframes fadeInScale {
    from {
        opacity: 0;
        transform: scale(0) rotate(90deg);
    }
    to {
        opacity: 1;
        transform: scale(1) rotate(0deg);
    }
}

/* 响应式优化 */
@media (max-width: 380px) {
    .mobile-navbar {
        height: 55px;
    }

    .nav-item .w-8 {
        width: 1.75rem;
        height: 1.75rem;
    }

    .nav-item svg {
        width: 1.25rem;
        height: 1.25rem;
    }

    /* 小屏幕下的子菜单调整 */
    .grid-cols-2 {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .submenu-item .flex.flex-col {
        padding: 0.75rem;
    }

    .submenu-item .w-10 {
        width: 2rem;
        height: 2rem;
    }

    .submenu-item .text-sm {
        font-size: 0.75rem;
    }
}

/* 中等屏幕优化 (平板) */
@media (min-width: 381px) and (max-width: 768px) {
    .submenu-item .flex.flex-col {
        padding: 0.875rem;
    }

    .submenu-item .w-10 {
        width: 2.25rem;
        height: 2.25rem;
    }
}

/* 子菜单最大高度优化 */
@media (max-height: 700px) {
    .max-h-80 {
        max-height: 60vh;
    }
}

@media (max-height: 600px) {
    .max-h-80 {
        max-height: 50vh;
    }
}

/* 深色模式优化 */
@media (prefers-color-scheme: dark) {
    .submenu-item::before {
        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
    }
}

/* 高对比度模式支持 */
@media (prefers-contrast: high) {
    .submenu-item {
        border: 2px solid hsl(var(--bc) / 0.2);
    }
    
    .submenu-item.bg-gradient-to-br {
        border: 2px solid hsl(var(--p));
    }
}
</style>
